// Turns the AI's template plan into a real Toons project: one slide per scene, designed with the
// layouts in aiLayouts.js, the theme's colours and the presenter (a library character with a
// talking mouth). The result is the editor's own slide format, saved like any other project.
//
// Besides the designed shapes and texts, a scene can use (all chosen by the AI, files sent by the
// API in the plan): a library background (animated or picture), a photo (Pixabay, stored on our
// server like a photo added in the editor), up to two animated props, a library illustration
// (Shapes panel), illustrations on the cards, and the editor's A to Z moves: the presenter walks in,
// props drop / fly / bounce / roll in or float. The plan's music becomes the background music.

import axios from "axios";
import { layoutFor, canFramePhoto } from "./aiLayouts";
import { themeFor, presenterFor } from "./aiThemes";
import {
  W, H, CANVAS, setCanvas, slideSize, character, prop as propLayer, libShape, photo as photoLayer, entrance, withMotion,
  shortenTo, FONT, SLIDE_PX,
} from "./aiShapes";
import { loadLottieJson } from "../lottieCache";
import { assetUrl } from "../assetUrl";
import { uploadDataUrl } from "../layerImages";
import { repaintFor, applyRepaint } from "./aiRecolor";

const ONE_PX_MS = 8.620689655172415; // 1000 ms / 116 px

// Hidden behind each scene's own background shape; the editor expects every slide to have one.
const SLIDE_BACKGROUND = {
  type: "image",
  color: null,
  Path: "Assets/DefaultBackground/CartoonBackground/637818315523971128.png",
  gradient: { Gcolor: null, FirstColor: null, SecondColor: null },
};

export const DEFAULT_WATERMARK = JSON.stringify({ added: false, src: "", position: "Top Left", opacity: 100, enabled: true, pattern: "" });
export const DEFAULT_MUSIC = JSON.stringify({ added: false, src: "", volume: 100, name: "", duration: "" });

/** The project's background music for the plan (the track the API chose), or no music. */
export function musicFor(plan) {
  const m = plan && plan.music;
  if (!m || !m.path) return DEFAULT_MUSIC;
  const name = String(m.name || "").replace(/\.mp3$/i, "");
  // Softer than full volume, so the music stays in the background.
  return JSON.stringify({ added: true, src: m.path, volume: 60, name, duration: Number(m.seconds) || 0 });
}

/**
 * Loads the Poppins font the scenes use (for measuring the texts and for the thumbnails). Texts
 * are fitted by measuring them, so this waits for the stylesheet and every weight to be ready;
 * measuring with a fallback font would make lines wrap differently in the editor.
 */
export async function ensureFont() {
  const weights = ["400", "500", "600", "700", "800"];
  // (document.fonts.check() also says "ready" when no Poppins font face exists yet, so look at the faces.)
  const loaded = (w) =>
    Array.from(document.fonts || []).some(
      (f) => f.family.replace(/["']/g, "") === "Poppins" && String(f.weight) === w && f.status === "loaded"
    );
  const ready = () => !document.fonts || weights.every(loaded);
  if (ready()) return;
  let link = document.querySelector(`link[href="${FONT.googleFontUrl}"]`);
  if (!link) {
    link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = FONT.googleFontUrl;
    document.head.appendChild(link);
  }
  const timeout = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
  try {
    // The font faces exist only once the stylesheet has loaded.
    await Promise.race([
      new Promise((resolve) => {
        if (link.sheet) return resolve();
        link.addEventListener("load", resolve, { once: true });
        link.addEventListener("error", resolve, { once: true });
      }),
      timeout(5000),
    ]);
    if (document.fonts && document.fonts.load) {
      await Promise.race([Promise.all(weights.map((w) => document.fonts.load(`${w} 16px Poppins`))), timeout(5000)]);
    }
  } catch (e) {
    /* falls back to the default font for measuring; the editor still shows Poppins */
  }
}

function slideBackground(bg) {
  if (!bg || !bg.path) return { ...SLIDE_BACKGROUND };
  return bg.type === "Animated" && bg.animationPath
    ? { type: "Animated", color: null, Path: bg.path, AnimationPath: bg.animationPath, gradient: { Gcolor: null, FirstColor: null, SecondColor: null } }
    : { type: "image", color: null, Path: bg.path, gradient: { Gcolor: null, FirstColor: null, SecondColor: null } };
}

/** A library character (chosen by the AI, sent by the API in plan.cast) in the builder's format. */
function libraryCharacter(c) {
  if (!c || !c.file) return null;
  return { id: c.id, w: 175, h: 220, file: c.file, image: c.image, poses: Array.isArray(c.poses) ? c.poses : [], look: c.look || null };
}

function makeSlide(index, layers, background) {
  layers.forEach((l, i) => {
    l.key = i + 1;
    l.layerIndex = i;
    l.index = i;
  });
  return {
    key: index,
    LayerData: layers,
    IsActive: index === 0,
    SlideBackground: background || { ...SLIDE_BACKGROUND },
    canvasWidth: slideSize().w,
    canvasHeight: slideSize().h,
    TotalLength: "00:00:10:00",
    SlideImage: "",
    SmallTime: "10.0 s",
    scaleEnd: SLIDE_PX,
    onePxEquivalent: ONE_PX_MS,
    startBackground: 0,
    endBackground: SLIDE_PX,
    TotalSlideWidth: SLIDE_PX,
    isSlideHeader: false,
    CurrentProject: null,
  };
}

const copy = (o) => (typeof structuredClone === "function" ? structuredClone(o) : JSON.parse(JSON.stringify(o)));

function authHeader() {
  try {
    const data = JSON.parse(localStorage.getItem("animation_studio_2.0_access_token"));
    return data && data.access_token ? { Authorization: "Bearer " + data.access_token } : {};
  } catch (e) {
    return {};
  }
}

// ------------------------------------------------------------------------- pictures

const ratioCache = new Map(); // url -> Promise<height / width>

/** Height / width of a library illustration (its SVG's own size). */
function pictureRatio(apiBaseURL, path) {
  const url = assetUrl(apiBaseURL, path);
  if (!ratioCache.has(url)) {
    const p = new Promise((resolve, reject) => {
      const img = new Image();
      const timer = setTimeout(() => reject(new Error("timeout")), 15000);
      img.onload = () => {
        clearTimeout(timer);
        resolve(img.naturalWidth > 0 && img.naturalHeight > 0 ? img.naturalHeight / img.naturalWidth : 1);
      };
      img.onerror = () => {
        clearTimeout(timer);
        reject(new Error("not loaded"));
      };
      img.src = url;
    });
    p.catch(() => ratioCache.delete(url));
    ratioCache.set(url, p);
  }
  return ratioCache.get(url);
}

// The key the editor's Photos panel uses (editor.component/.../images/PixabayPics.jsx).
const PIXABAY_KEY = "4581280-63c91ad658c69ede7692de09e";

/** The picture cut to the shape of its box (width / height = aspect, like CSS "cover"), as a JPEG data URL. */
function cropToAspect(dataUrl, aspect, maxWidth) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;
      let sw = iw;
      let sh = iw / aspect;
      if (sh > ih) {
        sh = ih;
        sw = ih * aspect;
      }
      const ow = Math.round(Math.min(maxWidth, sw));
      const oh = Math.round(ow / aspect);
      const canvas = document.createElement("canvas");
      canvas.width = ow;
      canvas.height = oh;
      canvas.getContext("2d").drawImage(img, (iw - sw) / 2, (ih - sh) / 2, sw, sh, 0, 0, ow, oh);
      resolve(canvas.toDataURL("image/jpeg", 0.86));
    };
    img.onerror = () => reject(new Error("photo not readable"));
    img.src = dataUrl;
  });
}

/**
 * A photo for the AI's search words, cut to the shape of its box (aspect = width / height) and
 * stored on our server like a photo added in the editor (Pixabay links expire):
 * { url (full address), path (address on the API) }, or null.
 */
async function findPhoto(apiBaseURL, query, aspect) {
  const orientation = aspect >= 1 ? "horizontal" : "vertical";
  if (!query || !String(query).trim()) return null;
  try {
    const q = encodeURIComponent(String(query).trim().slice(0, 90));
    const resp = await fetch(
      `https://pixabay.com/api/?key=${PIXABAY_KEY}&q=${q}&image_type=photo&orientation=${orientation}&safesearch=true&per_page=12`
    );
    if (!resp.ok) return null;
    const hits = ((await resp.json()) || {}).hits || [];
    if (!hits.length) return null;
    const hit = hits[Math.floor(Math.random() * Math.min(6, hits.length))];
    const source = orientation === "horizontal" ? hit.largeImageURL || hit.webformatURL : hit.webformatURL || hit.largeImageURL;
    // Downloaded through our API (the editor's way), then stored as a layer picture.
    const base = String(apiBaseURL).replace(/\/$/, "");
    const r = await axios.get(base + "/api/Images/ImageURLToBase64?url=" + source, { headers: authHeader() });
    if (!r.data || typeof r.data !== "string") return null;
    const type = /\.png($|\?)/i.test(source) ? "image/png" : "image/jpeg";
    const cropped = await cropToAspect(`data:${type};base64,${r.data}`, aspect, aspect >= 1 ? 1280 : 480);
    const url = await uploadDataUrl(apiBaseURL, cropped);
    return { url, path: url.slice(base.length + 1) };
  } catch (e) {
    console.log("AI template: photo not used.", query, e);
    return null;
  }
}

// ------------------------------------------------------------------------- moves

/** Gives the layer (placed at its end place) the entrance `kind` (AIPlanSpec.Motions). */
function applyEntrance(layer, kind) {
  const e = entrance(kind, layer.x, layer.y, layer.width, layer.height);
  layer.x = layer.originalX = Math.round(e.x);
  layer.y = layer.originalY = Math.round(e.y);
  withMotion(layer, e.motion);
  if (e.in) {
    layer.InEffect = "animated " + e.in + " ";
    layer.startTransitionWidth = Math.round(0.6 * 116);
  } else {
    layer.InEffect = "none";
    layer.startTransitionWidth = 0;
  }
  return layer;
}

/** The character's best action for walking (side walk first), or null when it has none. */
function walkPose(actor) {
  const rank = (n) => {
    const s = String(n || "").toLowerCase();
    if (/side.?walk/.test(s)) return 0;
    if (/^walk(ing)?$/.test(s)) return 1;
    if (/walk/.test(s) && !/angry/.test(s)) return 2;
    return 9;
  };
  const best = (actor.poses || []).filter((p) => p && p.poseFile && rank(p.poseName) < 9).sort((a, b) => rank(a.poseName) - rank(b.poseName))[0];
  return best || null;
}

// ------------------------------------------------------------------------- building

let canvasQueue = Promise.resolve();

/**
 * Runs fn (which builds and draws scenes) with the canvas set to `size` ("16:9", "9:16" or "1:1").
 * The canvas size is shared by the scene designs, so builds run one after the other here.
 */
export function withCanvas(size, fn) {
  const run = canvasQueue.then(async () => {
    setCanvas(size);
    try {
      return await fn();
    } finally {
      setCanvas("16:9");
    }
  });
  canvasQueue = run.catch(() => {});
  return run;
}

/**
 * Builds the slides. options: { apiBaseURL, withPresenter (false = never add one),
 * onScene(index, slide) called as each scene is ready }.
 */
export async function buildSlides(plan, { apiBaseURL, withPresenter = true, onScene } = {}) {
  if (!plan || !Array.isArray(plan.scenes) || plan.scenes.length === 0) throw new Error("The plan has no scenes.");
  await ensureFont();
  const theme = themeFor(plan.theme);

  // Presenter (and customer for the quote scene): library characters the AI chose, or a built-in
  // presenter when the API sent none (older plans, or the library is not set up).
  let P = null;
  let guest = null;
  if (withPresenter && plan.presenter !== "none") {
    P = plan.cast && plan.cast.presenter ? libraryCharacter(plan.cast.presenter) : presenterFor(plan);
    guest = plan.cast && plan.cast.guest ? libraryCharacter(plan.cast.guest) : null;
  }

  // Animations are downloaded once each (Utils/lottieCache). A file that cannot be loaded falls
  // back to the character's default pose, or leaves the character / prop out.
  const animationOf = async (file) => {
    try {
      return file ? await loadLottieJson(apiBaseURL, file) : null;
    } catch (e) {
      console.log("AI template: could not load", file, e);
      return null;
    }
  };
  if (P && !(await animationOf(P.file))) {
    console.log("AI template: presenter could not be loaded, building without one.");
    P = null;
    guest = null;
  }
  // New colours for the characters (skin, hair, clothes), the same in every pose.
  for (const who of [P, guest]) {
    if (!who || !who.look) continue;
    try {
      who.repaint = repaintFor(await animationOf(who.file), who.look);
    } catch (e) {
      console.log("AI template: character colours could not be changed.", e);
      who.repaint = null;
    }
  }
  const ratioOf = async (path) => {
    try {
      return await pictureRatio(apiBaseURL, path);
    } catch (e) {
      console.log("AI template: illustration not used.", path, e);
      return null;
    }
  };

  /** A character layer with its (repainted) animation. */
  const characterLayer = (actor, file, image, x, y, o) => {
    const layer = character({ ...actor, file, image }, x, y, o);
    return layer;
  };
  const finishCharacter = (layer, actor, animation) => {
    layer.JSONValue = copy(animation);
    if (actor.repaint) {
      applyRepaint(layer.JSONValue, actor.repaint);
      // Tells the thumbnail drawing (aiRender) to draw the repainted animation, not the library
      // picture. Not saved with the project.
      Object.defineProperty(layer, "_recolored", { value: true, enumerable: false, configurable: true });
    }
    return layer;
  };

  const slides = [];
  for (let i = 0; i < plan.scenes.length; i++) {
    const scene = plan.scenes[i];
    const layoutName = ["hero", "cards", "features", "stats", "quote", "offer", "cta"].includes(scene.layout) ? scene.layout : "cards";
    const layout = layoutFor(layoutName);
    const actor = scene.pose && scene.pose.actor === "guest" && guest ? guest : P;
    // Library backgrounds are 16:9 pictures and animations: on mobile and square they would be
    // stretched in the video, so those scenes use their photo or the design's own background.
    const libBackground = CANVAS === "16:9" && scene.background && scene.background.path ? scene.background : null;

    // Photo: in a frame (hero and features without a presenter), else as the background.
    const frameable = !actor && canFramePhoto() && (layoutName === "hero" || layoutName === "features");
    const wantsPhoto = typeof scene.photo === "string" && scene.photo.trim() && (frameable || !libBackground);
    // (The frames of hero and features are 3:4; a background has the canvas's shape.)
    const picture = wantsPhoto ? await findPhoto(apiBaseURL, scene.photo, frameable ? 0.75 : W / H) : null;
    const photoInFrame = !!(picture && frameable);
    const photoBackground = picture && !photoInFrame ? picture : null;

    // Props and the illustration, in that order (they take the layout's spots).
    const extras = [];
    for (const [p, kind] of [[scene.prop, scene.propMotion], [scene.prop2, scene.prop2Motion]]) {
      if (!p || typeof p !== "object" || !p.file) continue;
      const animation = await animationOf(p.file);
      if (animation) extras.push({ kind: "prop", item: p, animation, motion: kind || "pop" });
    }
    if (scene.decor && scene.decor.path) {
      const ratio = await ratioOf(scene.decor.path);
      if (ratio) extras.push({ kind: "shape", item: scene.decor, ratio, motion: scene.decorMotion || "float" });
    }
    const items = Array.isArray(scene.items) ? scene.items.slice(0, 3) : [];
    const arts = [];
    for (const it of items) arts.push(it && it.art && it.art.path ? { item: it.art, ratio: await ratioOf(it.art.path) } : null);

    const { layers, presenter, spots = [], photoSlot, artSlots = [] } = layout(scene, theme, actor, {
      bg: !!(libBackground || photoBackground),
      fill: extras.length,
      photo: photoInFrame,
      art: arts.map((a) => !!(a && a.ratio)),
    });

    if (photoInFrame && photoSlot) {
      layers.push(photoLayer(picture.url, photoSlot.x, photoSlot.y, photoSlot.w, photoSlot.h, { in: "zoomIn", at: photoSlot.at, name: "photo" }));
    }
    arts.forEach((a, k) => {
      const slot = artSlots[k];
      if (a && a.ratio && slot) layers.push(libShape(a.item, a.ratio, slot.x, slot.y, slot.w, slot.h, { in: "zoomIn", at: slot.at }));
    });
    extras.forEach((x, k) => {
      const slot = spots[k];
      if (!slot) return;
      const layer =
        x.kind === "prop"
          ? propLayer(x.item, copy(x.animation), slot.x, slot.y, slot.w, slot.h, { at: slot.at })
          : libShape(x.item, x.ratio, slot.x, slot.y, slot.w, slot.h, { at: slot.at });
      layers.push(applyEntrance(layer, x.motion));
    });

    if (actor && presenter) {
      // The action for this scene (wave, point, cheer...), else the default pose.
      let file = actor.file;
      let image = actor.image;
      let animation = null;
      if (scene.pose && scene.pose.file && scene.pose.file !== actor.file) {
        animation = await animationOf(scene.pose.file);
        if (animation) {
          file = scene.pose.file;
          image = scene.pose.image || actor.image;
        }
      }
      if (!animation) animation = await animationOf(actor.file);
      if (animation) {
        // Characters point to the right; on the right side of the scene they turn to the content.
        const pointing = /point|present|show|board|tablet/i.test((scene.pose && scene.pose.name) || "");
        const scale = presenter.scale || 1;
        const aw = actor.w * scale;
        const flip = presenter.flip || (pointing && presenter.x + aw / 2 > W * 0.55);
        let at = presenter.at;
        let effect = presenter.in;

        // Walks in from the nearer side (A to Z), then plays the scene's action where it stops:
        // the walk is its own layer, so the repainted colours are used while walking too.
        const walk = scene.entrance === "walk" ? walkPose(actor) : null;
        const walkAnimation = walk ? await animationOf(walk.poseFile) : null;
        if (walk && walkAnimation) {
          const fromRight = presenter.x + aw / 2 > W / 2;
          const startX = fromRight ? W + 6 : -aw - 6;
          const seconds = Math.max(1.2, Math.min(2.2, Math.abs(presenter.x - startX) / (W * 0.3)));
          const walker = characterLayer(actor, walk.poseFile, walk.poseImg || actor.image, startX, presenter.y, {
            at,
            flip: fromRight, // walks face right; coming from the right they face left
            scale,
            name: (actor === guest ? "Guest" : "Presenter") + " walking",
          });
          withMotion(walker, { toXf: presenter.x / W, toYf: presenter.y / H, duration: seconds, easing: "linear" });
          shortenTo(walker, seconds);
          layers.push(finishCharacter(walker, actor, walkAnimation));
          at += seconds;
          effect = null;
        }
        const ch = characterLayer(actor, file, image, presenter.x, presenter.y, {
          in: effect,
          at,
          flip,
          scale,
          name: actor === guest ? "Guest" : "Presenter",
        });
        layers.push(finishCharacter(ch, actor, animation));
      }
    }

    const background = libBackground
      ? slideBackground(libBackground)
      : photoBackground
      ? slideBackground({ type: "image", path: photoBackground.path })
      : null;
    const slide = makeSlide(i, layers, background);
    slides.push(slide);
    if (onScene) await onScene(i, slide);
  }
  return slides;
}
